import { useEffect, useMemo, useRef, useState } from "react";
import TkDatePicker from "../../../components/atoms/TkDatePicker";
import TkDataTable from "../../../components/molecules/TkDataTable";
import { IAPI_STA025_Response } from "../../../type/apiType";
import TkButton from "../../../components/atoms/TkButton";
import TkHelpIcon from "../../../components/atoms/TkHelpIcon";
import { Box } from "@mui/material";
import "../_style.scss";
import TkSelectForm from "../../../components/atoms/TkSelectForm";
import { selectPropsListData } from "../parts";

interface props {
  res: IAPI_STA025_Response;
  disabled: boolean;
  onClick: (e: any) => void;
  onChange: (e: any) => void;
  setDateErrorFlg: React.Dispatch<React.SetStateAction<boolean>>;
}

const monthMap = [
  { label: "apr", value: 4 },
  { label: "may", value: 5 },
  { label: "jun", value: 6 },
  { label: "jul", value: 7 },
  { label: "aug", value: 8 },
  { label: "sep", value: 9 },
  { label: "oct", value: 10 },
  { label: "nov", value: 11 },
  { label: "dec", value: 12 },
  { label: "jan", value: 1 },
  { label: "feb", value: 2 },
  { label: "mar", value: 3 },
];

const StaffListTable: React.FC<props> = ({
  res,
  disabled,
  onClick,
  onChange,
  setDateErrorFlg,
}) => {
  const [staffList, setStaffList] = useState(res.staffList ?? []);
  const saveStaffList = useRef(res.staffList ?? []);
  const updatedValuesSet = useRef(new Map<string, string>());
  const updatedTransferReasonSet = useRef(new Map<string, string>());
  const pageValueRef = useRef(0);

  useEffect(() => {
    if (res.staffList && res.staffList.length > 0) {
      res.staffList.forEach((item: any) => {
        item.year = res.unrecognizedYear;
      });
    }
    setStaffList(res.staffList ?? []);
    pageValueRef.current = 0;
    saveStaffList.current = res.staffList ?? [];
    onChange && onChange(res.staffList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [res.staffList, res.unrecognizedYear]);

  useEffect(() => {
    if (staffList) {
      const invalidReasons = ["2", "8", "10"];
      const validationDatas = staffList.filter((list: any) => {
        const r = list.transferReason;
        const d = list.retireDate;
        const date = new Date(d);
        const minDate = new Date("1900/01/01");
        const maxDate = new Date("2099/12/31");
        if (d === "Invalid Date") {
          return list;
        } else if (date < minDate || date > maxDate) {
          return list;
        } else if (invalidReasons.includes(r)) {
          return (r !== null && !d) || (r === null && d !== null);
        } else if (d != null) {
          return !invalidReasons.includes(r);
        } else {
          return false;
        }
      });
      if (validationDatas.length > 0) {
        setDateErrorFlg(true);
      } else {
        setDateErrorFlg(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffList]);

  const checkNeedNaShi = (item: any) => {
    return monthMap.some((m) => {
      let month = m.label as keyof typeof item;
      return item[month] === "1";
    });
  };

  const handleChange = (e: any, key: any, item: any) => {
    const newArr = saveStaffList.current.map((staff) =>
      staff.staffNo === item.staffNo
        ? { ...staff, [key]: e.target.value }
        : staff
    );

    if (key === "retireDate") {
      updatedValuesSet.current.set(item.staffNo, e?.target?.value);
    } else {
      updatedTransferReasonSet.current.set(item.staffNo, e?.target?.value);
    }
    saveStaffList.current = newArr;
    setStaffList(newArr);
    onChange && onChange(newArr);
  };

  const headList: any[] = [
    {
      headTitle: (
        <>
          氏名
          <TkHelpIcon
            helpMessage={
              <>
                登録されている被共済職員の氏名です。外字や「髙（はしごだか）」、「﨑（たつさき）」等の常用漢字でない文字は、正しく表示されていない恐れがあります。
              </>
            }
          />
        </>
      ),
      className:
        "SCRW060101DataTableSection__header SCRW060101DataTableSection__staffNo_header",
    },
    {
      headTitle: "生年月日",
      className:
        "SCRW060101DataTableSection__header SCRW060101DataTableSection__birthday_header",
    },
    {
      headTitle: "異動理由",
      className:
        "SCRW060101DataTableSection__header SCRW060101DataTableSection__transferReason_header",
    },
    {
      headTitle: "退職（予定）日",
      className:
        "SCRW060101DataTableSection__header SCRW060101DataTableSection__TkDatePicker_header",
    },
    {
      headTitle: "育休休業状況",
      dontNeedSort: true,
      className:
        "SCRW060101DataTableSection__header SCRW060101DataTableSection__TkButton_header",
    },
    {
      top: {
        headTitle: (
          <>
            {res.unrecognizedYear}年度　ならない月
            <TkHelpIcon
              helpMessage={
                <>
                  表示されている年度のならない月を登録してください。
                  <br />
                  ならない月とは、業務に従事した日数（従事したとみなす日を含む）が１０日を超えない月です。
                  <br />
                  詳しくは
                  <a
                    href={`${
                      process.env.PUBLIC_URL ?? ""
                    }/assets/pdf/≪別紙≫「なる月」「ならない月」について.pdf`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    こちら
                  </a>
                  をご確認ください。
                </>
              }
            />
          </>
        ),
      },
      bottom: [
        {
          headTitle: "無",
        },
        {
          headTitle: "4月",
        },
        {
          headTitle: "5月",
        },
        {
          headTitle: "6月",
        },
        {
          headTitle: "7月",
        },
        {
          headTitle: "8月",
        },
        {
          headTitle: "9月",
        },
        {
          headTitle: "10月",
        },
        {
          headTitle: "11月",
        },
        {
          headTitle: "12月",
        },
        {
          headTitle: "1月",
        },
        {
          headTitle: "2月",
        },
        {
          headTitle: "3月",
        },
      ],
    },
  ];

  let bodyList: any = useMemo(
    () =>
      staffList?.map((item: any) => {
        let transferReasonErrorMessage = "";
        let retireDateErrorMessage = "";
        let retireDateError = false;
        switch (item.transferReason) {
          case "2":
          case "8":
          case "10":
            //異動理由あり
            if (!item.retireDate) {
              //退職日入力なし
              retireDateErrorMessage = "退職（予定）日を入力してください";
              retireDateError = true;
            }
            break;
          default:
            //異動理由無し
            if (item.retireDate != null) {
              //退職日入力あり
              transferReasonErrorMessage = "異動理由を入力してください";
            }
        }

        return [
          {
            children: (item.staffNo ?? "") + ":" + (item.staffNameKanji ?? ""),
            forSort: item.staffNo,
            className: "SCRW060101DataTableSection__staffNo",
          },
          {
            children: item.birthday,
            forSort: item.birthday,
            className: "SCRW060101DataTableSection__birthday",
          },
          {
            children: (
              <TkSelectForm
                selectPropsList={selectPropsListData}
                wantToSetValue={item.transferReason}
                onChange={(e: any) => handleChange(e, "transferReason", item)}
                disabled={disabled}
                errorMessage={transferReasonErrorMessage}
              />
            ),
            forSort: item.transferReason,
            className: "SCRW060101DataTableSection__transferReason",
          },
          {
            children: (
              <TkDatePicker
                key={`${item.staffNo}`}
                wantToSetValue={item.retireDate}
                maxDate={new Date("2099/12/31")}
                minDate={new Date("1900/1/1")}
                disabled={disabled || item.loackState !== "0"}
                onChange={(e: any) => handleChange(e, "retireDate", item)}
                onGetErrorFlg={(e: any) => setDateErrorFlg(e)}
                errorMessage={retireDateErrorMessage}
                error={retireDateError}
              />
            ),
            className: "SCRW060101DataTableSection__TkDatePicker",
            forSort: item.retireDate,
          },
          {
            children: (
              <TkButton
                onClick={() => onClick(item.staffNo)}
                disabled={disabled}
              >
                育休確認・登録
              </TkButton>
            ),
            align: "center",
            className: "SCRW060101DataTableSection__TkButton",
          },
          {
            children: (
              <div
                className={`TkfakeCheckBox disabled ${
                  !checkNeedNaShi(item) && "checkmark clickDisabled"
                }`}
              />
            ),
            className: "SCRW060101DataTableSection__checkbox",
          },
          ...monthMap.map((m) => {
            const month = m.label as keyof typeof item;
            const itemValue = item[month];

            const classStrategy: any = {
              "1": "checkmark",
              "2": "disabled",
              "3": "checkmark clickDisabled",
            };

            const isLock = item.loackState === "1";
            const setClass = classStrategy[itemValue] || "";

            return {
              children: (
                <div
                  className={`TkfakeCheckBox ${setClass} ${
                    isLock || disabled ? "disabled" : ""
                  }`}
                  onClick={() =>
                    handleClickCheckBox(
                      itemValue !== "1",
                      item.staffNo,
                      m.label
                    )
                  }
                />
              ),
              className: "SCRW060101DataTableSection__checkbox",
              forSort: itemValue,
            };
          }),
        ];
      })!,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [staffList]
  );

  const handleClickCheckBox = (flag: any, staffNo: any, month: any) => {
    let returnArr: any = [];

    setStaffList(() => {
      returnArr = saveStaffList.current.map((item) =>
        item.staffNo === staffNo ? { ...item, [month]: flag ? "1" : "0" } : item
      );

      saveStaffList.current = returnArr;
      return returnArr;
    });

    onChange && onChange(returnArr);
  };

  const handleSortChange = (sort: any) => {
    if (!sort || sort.length === 0) return;

    const sortedArr = sort.map((item: any) => {
      return item[0].forSort;
    });

    const sortedStaffList = sortedArr
      .map((staffNo: any) =>
        staffList.find((staff) => staff.staffNo === staffNo)
      )
      .filter((staff: any) => staff !== undefined)
      .map((staff: any) => {
        if (staff) {
          if (updatedValuesSet.current.has(staff.staffNo)) {
            staff.retireDate = updatedValuesSet.current.get(staff.staffNo);
          }
          if (updatedTransferReasonSet.current.has(staff.staffNo)) {
            staff.transferReason = updatedTransferReasonSet.current.get(
              staff.staffNo
            );
          }
        }
        return staff;
      });

    saveStaffList.current = sortedStaffList;

    setStaffList(sortedStaffList);
  };

  const handlePageChange = (_: any, page: any) => {
    pageValueRef.current = page - 1;
  };

  return (
    <Box className="SCRW060101DataTableSection">
      <TkDataTable
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        headList={headList}
        bodyList={bodyList}
        pagin={true}
        dontResetPage
        setPageValue={pageValueRef.current}
      />
    </Box>
  );
};

export default StaffListTable;
